import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Images, RefreshCw, Merge, Loader2, AlertTriangle, Trash2 } from "lucide-react";

const API = `${getApiUrl()}/api`;

// Admin tool: find MDB image URLs that share identical byte content (same
// image re-uploaded to Cloudinary, or the same file hosted at both an Xbox
// CDN URL and a Cloudinary URL) and consolidate them onto a single canonical
// URL so the browser can cache one download for many achievements.
export default function MasterDatabaseImages() {
  const navigate = useNavigate();
  const [scanState, setScanState] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [autoScan, setAutoScan] = useState(false);
  const [consolidating, setConsolidating] = useState(null);
  const [selectedCanonical, setSelectedCanonical] = useState({}); // { sha256: url }
  const [broken, setBroken] = useState([]);
  const [pruning, setPruning] = useState(null);
  const [showBroken, setShowBroken] = useState(false);

  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      const res = await axios.get(`${API}/master/images/duplicates`);
      setGroups(res.data?.groups || []);
    } catch (e) {
      toast.error("Failed to load duplicate groups");
    } finally {
      setLoadingGroups(false);
    }
  }, []);

  const loadBroken = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/master/images/broken`);
      setBroken(res.data?.broken || []);
    } catch (e) {
      toast.error("Failed to load broken images");
    }
  }, []);

  useEffect(() => { loadGroups(); loadBroken(); }, [loadGroups, loadBroken]);

  const pruneBroken = async (url) => {
    if (!window.confirm(`Remove this URL from every reference in the MDB?\n\n${url}`)) return;
    setPruning(url);
    try {
      const res = await axios.delete(`${API}/master/images/broken`, { params: { url } });
      const total = res.data.achievement_updates + res.data.cover_updates + res.data.note_updates;
      toast.success(`Pruned ${total} reference${total === 1 ? "" : "s"}`);
      await loadBroken();
    } catch (e) {
      toast.error("Prune failed");
    } finally {
      setPruning(null);
    }
  };

  const runScanChunk = useCallback(async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/master/images/scan?max_new=200`);
      setScanState(res.data);
      if (res.data.new_hashed > 0) toast.success(`Hashed ${res.data.new_hashed} new URLs`);
      await loadGroups();
      await loadBroken();
      return res.data;
    } catch (e) {
      toast.error("Scan failed");
      return null;
    } finally {
      setScanning(false);
    }
  }, [loadGroups]);

  // Auto-scan loops until every URL has been hashed. Users start/stop it.
  useEffect(() => {
    if (!autoScan) return;
    let cancelled = false;
    (async () => {
      while (!cancelled) {
        const state = await runScanChunk();
        if (!state || state.remaining === 0) {
          setAutoScan(false);
          break;
        }
        await new Promise((r) => setTimeout(r, 400));
      }
    })();
    return () => { cancelled = true; };
  }, [autoScan, runScanChunk]);

  const chooseCanonical = (sha256, url) =>
    setSelectedCanonical((prev) => ({ ...prev, [sha256]: url }));

  const consolidate = async (group) => {
    const canonical = selectedCanonical[group.sha256] || group.canonical.url;
    const aliases = [
      group.canonical.url,
      ...group.aliases.map((a) => a.url),
    ].filter((u) => u !== canonical);
    setConsolidating(group.sha256);
    try {
      const res = await axios.post(`${API}/master/images/consolidate`, {
        canonical,
        aliases,
      });
      toast.success(
        `Rewrote ${res.data.achievement_updates + res.data.cover_updates + res.data.note_updates} references onto the canonical URL`
      );
      await loadGroups();
    } catch (e) {
      toast.error("Consolidation failed");
    } finally {
      setConsolidating(null);
    }
  };

  const totalRefsSavable = groups.reduce(
    (n, g) => n + g.aliases.reduce((s, a) => s + a.refs, 0), 0
  );

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/master-database")}
            className="text-white/60 hover:text-white"
            data-testid="images-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Images className="w-5 h-5 text-pink-400" />
            <h1 className="text-xl font-semibold">Duplicate Image Finder</h1>
          </div>
        </div>

        <p className="text-sm text-white/60 max-w-2xl">
          Finds achievement images that share identical byte content across different URLs
          (typical when the same file is re-uploaded to Cloudinary or exists on both Xbox
          and Cloudinary). Consolidating references onto a single canonical URL lets the
          browser cache one download for many achievements — dramatically faster game
          pages with 200+ achievements.
        </p>

        <div
          className="bg-[#121212] border border-white/5 rounded-lg p-4 space-y-3"
          data-testid="images-scan-panel"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Content-hash scan</div>
              <div className="text-xs text-white/50">
                {scanState
                  ? `${scanState.cached + scanState.new_hashed} / ${scanState.total_urls} URLs hashed · ${scanState.remaining} remaining`
                  : "Click Scan to start hashing image bytes."}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={runScanChunk}
                disabled={scanning || autoScan}
                data-testid="images-scan-chunk"
              >
                {scanning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Scan 200
              </Button>
              <Button
                onClick={() => setAutoScan((v) => !v)}
                variant={autoScan ? "destructive" : "default"}
                data-testid="images-scan-auto"
              >
                {autoScan ? "Stop Auto-Scan" : "Auto-Scan All"}
              </Button>
            </div>
          </div>
          {scanState && scanState.failed > 0 && (
            <div className="text-[11px] text-amber-300/80">
              {scanState.failed} URLs returned network errors this pass — they&apos;ll be skipped
              (broken / offline images). Re-run scan later to retry.
            </div>
          )}
          <div className="text-[11px] text-white/40 pt-1 border-t border-white/5">
            Nightly job: <span className="text-white/60">runs every Saturday at 03:00 UTC</span>,
            hashes up to 500 new URLs per run. Broken URLs are collected in the Broken Images
            report below and never auto-retried.
          </div>
        </div>

        {/* Broken Images report — non-auto-retried failures for manual triage */}
        <div
          className="bg-[#121212] border border-white/5 rounded-lg p-4 space-y-3"
          data-testid="broken-images-panel"
        >
          <button
            type="button"
            onClick={() => setShowBroken((v) => !v)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium">Broken Images</span>
              <span className="text-[11px] text-white/50">
                ({broken.length}
                {broken.length ? ` · ${broken.reduce((s, b) => s + b.ref_count, 0)} refs` : ""})
              </span>
            </div>
            <span className="text-[11px] text-white/40">{showBroken ? "Hide" : "Show"}</span>
          </button>
          {showBroken && (
            <div className="space-y-1.5">
              {broken.length === 0 && (
                <p className="text-xs text-white/40 italic">
                  No broken images recorded yet. The nightly scan or a manual scan flags URLs
                  that fail to download.
                </p>
              )}
              {broken.map((b) => (
                <div
                  key={b.url}
                  className="flex items-start gap-2 p-2 rounded border border-white/5 hover:border-amber-400/30 transition-colors"
                  data-testid={`broken-row-${b.url.slice(-12)}`}
                >
                  <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0 text-xs">
                    <div className="truncate text-white/80">{b.url}</div>
                    <div className="text-white/40">
                      {b.ref_count} reference{b.ref_count === 1 ? "" : "s"}
                      {b.refs.length > 0 && (
                        <>
                          {" · "}
                          {b.refs.map((r) => r.name).filter(Boolean).slice(0, 3).join(", ")}
                          {b.has_more_refs && "…"}
                        </>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-red-500/30 text-red-300 hover:bg-red-500/10 shrink-0"
                    onClick={() => pruneBroken(b.url)}
                    disabled={pruning === b.url}
                    data-testid={`broken-prune-${b.url.slice(-12)}`}
                    title="Remove this URL from every reference and drop it from the hash cache"
                  >
                    {pruning === b.url
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Trash2 className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm">
            <span className="text-white/70 font-medium">{groups.length}</span>{" "}
            <span className="text-white/50">duplicate group{groups.length === 1 ? "" : "s"}</span>
            {totalRefsSavable > 0 && (
              <span className="text-white/50">
                {" "}· <span className="text-emerald-300 font-medium">{totalRefsSavable}</span> refs consolidatable
              </span>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={loadGroups} disabled={loadingGroups}>
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loadingGroups ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {loadingGroups && groups.length === 0 && (
          <div className="text-center py-12 text-white/40">Loading…</div>
        )}
        {!loadingGroups && groups.length === 0 && (
          <div className="text-center py-12 text-white/40 border border-white/5 rounded-lg">
            No duplicates found yet. Run a scan to hash more URLs.
          </div>
        )}

        {groups.map((g) => {
          const chosen = selectedCanonical[g.sha256] || g.canonical.url;
          const allUrls = [g.canonical, ...g.aliases];
          return (
            <div
              key={g.sha256}
              className="bg-[#121212] border border-white/5 rounded-lg p-4 space-y-3"
              data-testid={`dup-group-${g.sha256.slice(0, 8)}`}
            >
              <div className="flex items-center justify-between">
                <div className="text-xs text-white/50">
                  <code className="text-white/70">{g.sha256.slice(0, 12)}…</code>{" "}
                  · {g.total_urls} URLs · {g.total_refs} references
                  {g.size_bytes ? ` · ${Math.round(g.size_bytes / 1024)} KB each` : ""}
                </div>
                <Button
                  onClick={() => consolidate(g)}
                  disabled={consolidating === g.sha256}
                  data-testid={`dup-consolidate-${g.sha256.slice(0, 8)}`}
                >
                  {consolidating === g.sha256
                    ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    : <Merge className="w-4 h-4 mr-2" />}
                  Consolidate
                </Button>
              </div>
              <div className="space-y-1.5">
                {allUrls.map((item) => {
                  const isChosen = item.url === chosen;
                  return (
                    <label
                      key={item.url}
                      className={`flex items-center gap-2 p-2 rounded border ${
                        isChosen
                          ? "border-emerald-400/40 bg-emerald-500/5"
                          : "border-white/5 hover:border-white/10"
                      }`}
                    >
                      <input
                        type="radio"
                        checked={isChosen}
                        onChange={() => chooseCanonical(g.sha256, item.url)}
                        className="shrink-0"
                        data-testid={`dup-pick-${g.sha256.slice(0, 8)}`}
                      />
                      <img
                        src={item.url}
                        alt=""
                        className="w-8 h-8 rounded object-cover bg-black/60 shrink-0"
                        loading="lazy"
                      />
                      <div className="flex-1 min-w-0 text-xs">
                        <div className="truncate text-white/80">{item.url}</div>
                        <div className="text-white/40">
                          {item.refs} reference{item.refs === 1 ? "" : "s"}
                        </div>
                      </div>
                      {isChosen && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-200 border border-emerald-400/30 shrink-0">
                          Canonical
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
